import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ReminderToast, isBadMomentForReminder } from "@/components/ui/ReminderToast";
import { useAuth } from "@/context/AuthContext";
import { usePaywall } from "@/lib/paywall";
import { DISCOUNT, PRICING, activeDiscountUntil, formatDiscountDeadline } from "@/lib/pricing";

const MESSAGES = [
  "Sua vida financeira pode começar a mudar hoje. Só falta a assinatura.",
  "Quem anota todo dia descobre pra onde o dinheiro vai. Que tal começar hoje?",
  `Por menos de R$ ${PRICING.yearlyPerDayUnder} por dia, você tem o controle do mês inteiro.`,
  `No plano anual sai R$ ${PRICING.yearlyPerMonth} por mês — menos que um lanche.`,
  "Cada mês sem controle é dinheiro que não volta. Bora mudar isso?",
  "Metas, orçamento e calendário já estão prontos. Só falta você começar.",
  "Imagina fechar o mês sabendo exatamente pra onde foi cada real.",
  "Um minuto por dia anotando muda o jeito que você gasta.",
  "Você está a um passo de ver suas finanças com clareza.",
  `Assinando o anual, você economiza R$ ${PRICING.yearlySavings} em relação ao mensal.`,
  "Organizar as contas hoje é dormir mais tranquilo amanhã.",
  "Seu primeiro lançamento está a uma assinatura de distância.",
];

// Pra quem ganhou o desconto do admin: os lembretes falam do desconto e de até quando ele vale.
function discountMessages(until: Date) {
  const deadline = formatDiscountDeadline(until);
  return [
    `Você ganhou ${DISCOUNT.yearlyPercent}% de desconto no plano anual. Vale até ${deadline}.`,
    `Seu desconto especial: primeiro ano por R$ ${DISCOUNT.yearlyFirst} em vez de R$ ${PRICING.yearly}. Até ${deadline}.`,
    `Primeiro mês por R$ ${DISCOUNT.monthlyFirst} — desconto só seu, válido até ${deadline}.`,
  ];
}

// Frequência: o primeiro aparece depois de um tempo navegando, os seguintes bem espaçados, e
// no máximo alguns por visita - lembrete demais vira irritação e a pessoa fecha o app.
const FIRST_DELAY_MS = 25_000;
const INTERVAL_MS = 3 * 60_000;
const MAX_PER_VISIT = 3;
const RETRY_MS = 15_000;
const STORAGE_KEY = "controlenext-reminders";

interface VisitState {
  start: number;
  shown: number;
  last: number;
  offset: number;
}

// Guardado por aba (sessionStorage): recarregar a página não reinicia a contagem da visita.
function readState(): VisitState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as VisitState;
  } catch {
    // sem sessionStorage: cada carregamento conta como uma visita nova
  }
  const fresh = { start: Date.now(), shown: 0, last: 0, offset: Math.floor(Math.random() * MESSAGES.length) };
  writeState(fresh);
  return fresh;
}

function writeState(state: VisitState) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // idem
  }
}

/**
 * Lembretes em formato de notificação pra quem ainda não assinou (visitante da demonstração ou
 * conta sem assinatura), com frases curtas puxando pra assinatura. O botão segue a mesma regra
 * do resto do app: visitante vai pro cadastro, conta sem assinatura vai pros planos.
 */
export function SubscribeReminder() {
  const { user } = useAuth();
  const { guard } = usePaywall();
  const navigate = useNavigate();
  const location = useLocation();
  const [message, setMessage] = useState<string | null>(null);
  const pathRef = useRef(location.pathname);
  pathRef.current = location.pathname;
  const discountUntil = activeDiscountUntil(user?.discountUntil);
  const discountRef = useRef(discountUntil);
  discountRef.current = discountUntil;

  const eligible = !user?.isPremium;

  useEffect(() => {
    if (!eligible) return;
    let timer: number | undefined;

    const attempt = () => {
      // Não interrompe quem já está nos planos, com um formulário aberto ou com a aba em segundo
      // plano - tenta de novo daqui a pouco.
      if (pathRef.current === "/premium" || isBadMomentForReminder()) {
        timer = window.setTimeout(attempt, RETRY_MS);
        return;
      }
      const state = readState();
      const pool = discountRef.current ? discountMessages(discountRef.current) : MESSAGES;
      setMessage(pool[(state.offset + state.shown) % pool.length]);
      writeState({ ...state, shown: state.shown + 1, last: Date.now() });
      schedule();
    };

    const schedule = () => {
      const state = readState();
      if (state.shown >= MAX_PER_VISIT) return;
      const due = state.last ? state.last + INTERVAL_MS : state.start + FIRST_DELAY_MS;
      timer = window.setTimeout(attempt, Math.max(due - Date.now(), 1000));
    };

    schedule();
    return () => window.clearTimeout(timer);
  }, [eligible]);

  if (!eligible || !message) return null;

  return (
    <ReminderToast
      message={message}
      actionLabel={!user ? "Criar minha conta" : discountUntil ? "Ver meu desconto" : "Ver planos"}
      onAction={() => {
        setMessage(null);
        guard(() => navigate("/premium"));
      }}
      onClose={() => setMessage(null)}
    />
  );
}
